import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import { PartnerService } from "@/services/partnerService";
import { Handshake, Upload, ArrowLeft, CheckCircle } from "lucide-react";
import InputMask from "react-input-mask";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CountrySelect, Country } from "@/components/CountrySelect";

const PartnerRegistration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    description: "",
    social_media: "",
    service_type: "",
  });
  const [phoneDdi, setPhoneDdi] = useState("55"); // Brasil como padrão
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validação de email
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        setEmailError("Por favor, insira um email válido");
      } else {
        setEmailError("");
      }
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Erro",
          description: "Por favor, selecione um arquivo de imagem",
          variant: "destructive",
        });
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "A imagem deve ter no máximo 5MB",
          variant: "destructive",
        });
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha o nome do parceiro",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha o email",
        variant: "destructive",
      });
      return;
    }

    if (emailError) {
      toast({
        title: "Erro",
        description: "Por favor, corrija o email",
        variant: "destructive",
      });
      return;
    }

    if (!formData.phone.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha o telefone",
        variant: "destructive",
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha a descrição",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Criar parceiro com status 'pendente'
      const partnerId = await PartnerService.createPartner({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim() || null,
        website: formData.website.trim() || null,
        description: formData.description.trim(),
        social_media: formData.social_media.trim() || null,
        service_type: formData.service_type.trim() || null,
        is_active: true,
        status: "pendente",
        logo: null,
      });

      // Fazer upload da logo se houver
      if (logoFile && partnerId) {
        try {
          await PartnerService.uploadLogo(partnerId, logoFile);
        } catch (logoError) {
          console.error("Erro ao fazer upload da logo:", logoError);
          // Não falhar o cadastro se o upload da logo falhar
        }
      }

      toast({
        title: "Cadastro realizado com sucesso!",
        description: "Seu cadastro foi enviado e está aguardando aprovação. Você receberá um email quando for aprovado.",
      });

      // Limpar formulário
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        website: "",
        description: "",
        social_media: "",
        service_type: "",
      });
      setLogoFile(null);
      setLogoPreview(null);

      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error: any) {
      console.error("Erro ao cadastrar parceiro:", error);
      toast({
        title: "Erro ao cadastrar",
        description: error.message || "Ocorreu um erro ao realizar o cadastro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        isAuthenticated={isAuthenticated}
        isOwner={userType === "owner"}
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Handshake className="h-8 w-8 text-orange-500" />
            <h1 className="text-3xl font-bold">Seja nosso Parceiro</h1>
          </div>
          <p className="text-muted-foreground">
            Preencha o formulário abaixo para se tornar um parceiro do Live Stay.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações do Parceiro</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="name">Nome do Parceiro / Empresa *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ex: Restaurante Sushi Muro Alto"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contato@empresa.com.br"
                    className={emailError ? "border-destructive" : ""}
                    required
                    disabled={isSubmitting}
                  />
                  {emailError && (
                    <p className="text-sm text-destructive mt-1">{emailError}</p>
                  )}
                </div>

                {/* Telefone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone *</Label>
                  <div className="flex gap-2">
                    <CountrySelect
                      value={phoneDdi}
                      onValueChange={(ddi: string, country: Country) => {
                        setPhoneDdi(ddi);
                      }}
                      disabled={isSubmitting}
                    />
                    <InputMask
                      mask="(99) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => handleChange({ target: { name: "phone", value: e.target.value } } as any)}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(81) 99999-9999"
                          required
                          disabled={isSubmitting}
                          className="flex-1"
                        />
                      )}
                    </InputMask>
                  </div>
                </div>

                {/* Endereço */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="address">Endereço</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Av. Beira Mar, 123 - Muro Alto, Porto de Galinhas - PE"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Website */}
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.empresa.com.br"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Redes Sociais */}
                <div className="space-y-2">
                  <Label htmlFor="social_media">Redes Sociais</Label>
                  <Input
                    id="social_media"
                    name="social_media"
                    value={formData.social_media}
                    onChange={handleChange}
                    placeholder="@empresa ou link do perfil"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Tipo de Serviço */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="service_type">Tipo de Serviço</Label>
                  <Select
                    value={formData.service_type || "none"}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, service_type: value === "none" ? "" : value }))}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de serviço" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      <SelectItem value="Restaurantes">Restaurantes</SelectItem>
                      <SelectItem value="Farmácias">Farmácias</SelectItem>
                      <SelectItem value="Transfer">Transfer</SelectItem>
                      <SelectItem value="Passeios">Passeios</SelectItem>
                      <SelectItem value="Fotógrafos">Fotógrafos</SelectItem>
                      <SelectItem value="Salões de Beleza">Salões de Beleza</SelectItem>
                      <SelectItem value="Massagem">Massagem</SelectItem>
                      <SelectItem value="Aluguel de Equipamentos">Aluguel de Equipamentos</SelectItem>
                      <SelectItem value="Supermercados">Supermercados</SelectItem>
                      <SelectItem value="Padarias">Padarias</SelectItem>
                      <SelectItem value="Disk Bebidas">Disk Bebidas</SelectItem>
                      <SelectItem value="Outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Logo */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="logo">Logo do Parceiro</Label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Input
                        id="logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        disabled={isSubmitting}
                        className="cursor-pointer"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
                      </p>
                    </div>
                    {logoPreview && (
                      <div className="w-24 h-24 border rounded-lg overflow-hidden">
                        <img
                          src={logoPreview}
                          alt="Preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Descrição */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="description">Descrição *</Label>
                  <div className="border rounded-md">
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                      placeholder="Descreva seu negócio, serviços oferecidos e como você pode agregar valor aos nossos hóspedes..."
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'align': ['', 'center'] }]
                        ]
                      }}
                      formats={[
                        'bold', 
                        'italic', 
                        'underline',
                        'list',
                        'bullet',
                        'align'
                      ]}
                      style={{ minHeight: '150px' }}
                      readOnly={isSubmitting}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Esta descrição será exibida no site quando seu cadastro for aprovado. Use o editor para formatar o texto com negrito, itálico, sublinhado, listas numeradas, marcadores e alinhamento.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                <p className="text-sm text-orange-800">
                  <strong>Importante:</strong> Seu cadastro será analisado pela nossa equipe. 
                  Nós entraremos em contato assim que o seu contrato for efetivado.
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Enviar Cadastro
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PartnerRegistration;

